/**
 * Canvas2DCamera — engine-neutral 2D pan/zoom camera with animation support.
 *
 * Kept dependency-light: no extension system, no engine reference, no
 * viewport ownership — callers pass `viewportWidth` / `viewportHeight` into
 * the methods that need them. Any 2D canvas engine (graph, timeline, 2D
 * map) can own an instance and drive it from its own pointer/keyboard
 * handlers.
 *
 * Animation uses `cubicOut` from the shared easings module; `onTick`
 * callbacks are how the caller schedules re-renders between animation
 * frames — the camera itself does no rendering.
 */

import { cubicOut } from "@/components/ecv2/engine/canvasEngine/easings";

export type CameraRect = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type CameraPoint = { x: number; y: number };

export class Canvas2DCamera {
  public offsetX = 0;
  public offsetY = 0;
  public scale = 1;

  // ---- Animation state ----
  private animating = false;
  private animStartTime = 0;
  private animDuration = 0;
  private animFromOffsetX = 0;
  private animFromOffsetY = 0;
  private animFromScale = 0;
  private animToOffsetX = 0;
  private animToOffsetY = 0;
  private animToScale = 0;

  constructor(
    public minScale = 0.1,
    public maxScale = 3
  ) {}

  get isAnimating(): boolean {
    return this.animating;
  }

  /** Tick the animation forward. Returns true if still animating. */
  tickAnimation(): boolean {
    if (!this.animating) {
      return false;
    }
    const elapsed = performance.now() - this.animStartTime;
    const t = Math.min(elapsed / this.animDuration, 1);
    const e = cubicOut(t);

    this.offsetX = this.animFromOffsetX + (this.animToOffsetX - this.animFromOffsetX) * e;
    this.offsetY = this.animFromOffsetY + (this.animToOffsetY - this.animFromOffsetY) * e;
    this.scale = this.animFromScale + (this.animToScale - this.animFromScale) * e;

    if (t >= 1) {
      this.animating = false;
    }
    return this.animating;
  }

  /** Stop any in-progress animation, snapping to end state. */
  stopAnimation(): void {
    if (this.animating) {
      this.offsetX = this.animToOffsetX;
      this.offsetY = this.animToOffsetY;
      this.scale = this.animToScale;
      this.animating = false;
    }
  }

  worldToScreen(wx: number, wy: number): CameraPoint {
    return {
      x: wx * this.scale + this.offsetX,
      y: wy * this.scale + this.offsetY,
    };
  }

  screenToWorld(sx: number, sy: number): CameraPoint {
    return {
      x: (sx - this.offsetX) / this.scale,
      y: (sy - this.offsetY) / this.scale,
    };
  }

  pan(dx: number, dy: number): void {
    this.stopAnimation();
    this.offsetX += dx;
    this.offsetY += dy;
  }

  /** Zoom at a screen-space point. Positive delta = zoom out.
   *  Exponential curve so zoom is proportional to gesture magnitude and
   *  symmetric — zooming in N steps then out N steps returns to the same
   *  scale. Divisor of 100 matches Figma's trackpad feel. */
  zoomAt(screenX: number, screenY: number, delta: number): void {
    this.stopAnimation();
    const zoomFactor = Math.pow(2, -delta / 100);
    const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * zoomFactor));
    if (newScale === this.scale) {
      return;
    }

    const worldX = (screenX - this.offsetX) / this.scale;
    const worldY = (screenY - this.offsetY) / this.scale;
    this.scale = newScale;
    this.offsetX = screenX - worldX * this.scale;
    this.offsetY = screenY - worldY * this.scale;
  }

  /** Fit a world-space bounding rect into the viewport with padding (0–1 fraction). */
  fitRect(bounds: CameraRect, viewportWidth: number, viewportHeight: number, padding = 0.15): void {
    const target = this.computeFitRect(bounds, viewportWidth, viewportHeight, padding);
    if (!target) {
      return;
    }
    this.scale = target.scale;
    this.offsetX = target.offsetX;
    this.offsetY = target.offsetY;
  }

  /** Animated version of fitRect. */
  animateFitRect(
    bounds: CameraRect,
    viewportWidth: number,
    viewportHeight: number,
    onTick: () => void,
    durationMs = 400,
    padding = 0.15
  ): void {
    const target = this.computeFitRect(bounds, viewportWidth, viewportHeight, padding);
    if (!target) {
      return;
    }
    this.animateTo(target.offsetX, target.offsetY, target.scale, durationMs, onTick);
  }

  /** Animate to a specific world point (centered in viewport). */
  animateToWorldPoint(
    wx: number,
    wy: number,
    targetScale: number,
    viewportWidth: number,
    viewportHeight: number,
    onTick: () => void,
    durationMs = 350
  ): void {
    const s = Math.min(this.maxScale, Math.max(this.minScale, targetScale));
    const ox = viewportWidth / 2 - wx * s;
    const oy = viewportHeight / 2 - wy * s;
    this.animateTo(ox, oy, s, durationMs, onTick);
  }

  /** Zoom in/out by a step, animated, centered on viewport center. */
  animateZoomStep(
    direction: 1 | -1,
    viewportWidth: number,
    viewportHeight: number,
    onTick: () => void,
    durationMs = 200
  ): void {
    this.stopAnimation();
    const factor = direction > 0 ? 1.4 : 1 / 1.4;
    const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * factor));
    const cx = viewportWidth / 2;
    const cy = viewportHeight / 2;
    const worldX = (cx - this.offsetX) / this.scale;
    const worldY = (cy - this.offsetY) / this.scale;
    const ox = cx - worldX * newScale;
    const oy = cy - worldY * newScale;
    this.animateTo(ox, oy, newScale, durationMs, onTick);
  }

  getVisibleWorldRect(viewportWidth: number, viewportHeight: number): CameraRect {
    const topLeft = this.screenToWorld(0, 0);
    const bottomRight = this.screenToWorld(viewportWidth, viewportHeight);
    return {
      minX: topLeft.x,
      minY: topLeft.y,
      maxX: bottomRight.x,
      maxY: bottomRight.y,
    };
  }

  // ---- Private helpers ----

  private computeFitRect(
    bounds: CameraRect,
    viewportWidth: number,
    viewportHeight: number,
    padding: number
  ): { offsetX: number; offsetY: number; scale: number } | null {
    const bw = bounds.maxX - bounds.minX;
    const bh = bounds.maxY - bounds.minY;
    if (bw <= 0 || bh <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
      return null;
    }
    const padFraction = 1 - padding * 2;
    const scaleX = (viewportWidth * padFraction) / bw;
    const scaleY = (viewportHeight * padFraction) / bh;
    const s = Math.min(scaleX, scaleY, this.maxScale);
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    return {
      scale: s,
      offsetX: viewportWidth / 2 - centerX * s,
      offsetY: viewportHeight / 2 - centerY * s,
    };
  }

  private animateTo(
    toOffsetX: number,
    toOffsetY: number,
    toScale: number,
    durationMs: number,
    onTick: () => void
  ): void {
    this.animFromOffsetX = this.offsetX;
    this.animFromOffsetY = this.offsetY;
    this.animFromScale = this.scale;
    this.animToOffsetX = toOffsetX;
    this.animToOffsetY = toOffsetY;
    this.animToScale = toScale;
    this.animStartTime = performance.now();
    this.animDuration = durationMs;
    this.animating = true;
    onTick();
  }
}
